import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TimeEntriesService } from '../../../shared/services/time-entries.service';
import { TauriService } from '../../../shared/services/tauri.service';
import { TimeEntry, TimelineTask } from '../../../shared/models/activity.model';
import { Subscription } from 'rxjs';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';

interface DailySummary {
  date: Date;
  dateString: string;
  totalHours: number;
  totalMinutes: number;
  formattedTotal: string;
  projectTimeHours: number;
  projectTimeMinutes: number;
  formattedProjectTime: string;
  capturedTimeHours: number;
  capturedTimeMinutes: number;
  formattedCapturedTime: string;
  entries: TimeEntry[];
  projectBreakdown: { projectName: string; hours: number; minutes: number; formattedDuration: string; percentage: number }[];
}

@Component({
  selector: 'app-daily-summary',
  standalone: true,
  imports: [CommonModule, DateInputsModule],
  templateUrl: './daily-summary.html',
  styleUrl: './daily-summary.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DailySummaryComponent implements OnInit, OnDestroy {
  private timeEntriesService = inject(TimeEntriesService);
  private tauriService = inject(TauriService);
  private cdr = inject(ChangeDetectorRef);

  dailySummaries: DailySummary[] = [];
  selectedDate: Date = new Date();
  selectedWeekStart: Date = new Date();
  private subscriptions: Subscription[] = [];
  isLoading = false;
  private allEntries: TimeEntry[] = [];
  private allTimelineTasks: TimelineTask[] = [];

  protected Math = Math;

  ngOnInit() {
    this.selectedDate = new Date();
    this.selectedDate.setHours(0, 0, 0, 0);
    this.calculateWeekStart();

    // Subscribe to time entries changes
    this.subscriptions.push(
      this.timeEntriesService.timeEntries$.subscribe((entries) => {
        this.allEntries = entries;
        this.cdr.markForCheck();
      })
    );

    // Subscribe to timeline tasks (Memtime activities)
    this.subscriptions.push(
      this.tauriService.timelineTasks$.subscribe((tasks) => {
        this.allTimelineTasks = tasks;
        this.cdr.markForCheck();
      })
    );

    this.loadData();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private calculateWeekStart() {
    const date = new Date(this.selectedDate);
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day; // Adjust when day is Sunday
    this.selectedWeekStart = new Date(date);
    this.selectedWeekStart.setDate(date.getDate() + diff);
    this.selectedWeekStart.setHours(0, 0, 0, 0);
  }

  async loadData() {
    console.log('[DailySummary] Starting loadData...');
    this.isLoading = true;
    this.cdr.markForCheck();

    // Load entries for the entire week range
    const startDate = new Date(this.selectedWeekStart);
    const endDate = new Date(this.selectedWeekStart);
    endDate.setDate(endDate.getDate() + 6);
    endDate.setHours(23, 59, 59, 999);

    console.log('[DailySummary] Loading entries from', startDate, 'to', endDate);

    try {
      // Load entries for the full week range
      await this.timeEntriesService.loadEntriesForDateRange(
        startDate.toISOString(),
        endDate.toISOString()
      );

      console.log('[DailySummary] Entries loaded, waiting for observable update...');

      // Wait a bit for the observable to update
      await new Promise(resolve => setTimeout(resolve, 100));

      console.log('[DailySummary] All entries:', this.allEntries.length);

      // Create summaries for each day
      const summaries: DailySummary[] = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        const dateStr = this.getLocalDateString(date);

        // Load TimelineTask for this specific day
        await this.tauriService.getTasksForDate(date);
        await new Promise(resolve => setTimeout(resolve, 50));

        const entries = this.allEntries.filter((entry: TimeEntry) => {
          const entryDate = new Date(entry.startTime);
          return this.getLocalDateString(entryDate) === dateStr;
        });

        const tasks = this.allTimelineTasks.filter((task: TimelineTask) => {
          const taskDate = new Date(task.startTime);
          return this.getLocalDateString(taskDate) === dateStr;
        });

        console.log(`[DailySummary] Day ${i} (${dateStr}): ${entries.length} entries, ${tasks.length} tasks`);

        const summary = this.calculateDailySummary(date, entries, tasks);
        summaries.push(summary);
      }

      this.dailySummaries = summaries;
      console.log('[DailySummary] Summaries created:', summaries.length);
    } catch (error) {
      console.error('[DailySummary] Error loading week data:', error);
      // Create empty summaries on error
      const summaries: DailySummary[] = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        summaries.push(this.calculateDailySummary(date, [], []));
      }
      this.dailySummaries = summaries;
    } finally {
      this.isLoading = false;
      console.log('[DailySummary] Loading complete, isLoading:', this.isLoading);
      this.cdr.markForCheck();
    }
  }

  private calculateDailySummary(date: Date, entries: TimeEntry[], tasks: TimelineTask[] = []): DailySummary {
    let totalMinutes = 0;
    let projectTimeMinutes = 0; // Temps enregistré manuellement (TimeEntry)
    let capturedTimeMinutes = 0; // Temps capturé automatiquement (TimelineTask)
    const projectMap = new Map<string, number>();

    // Calculer le Project Time à partir des TimeEntry
    entries.forEach(entry => {
      const duration = (new Date(entry.endTime).getTime() - new Date(entry.startTime).getTime()) / 60000;
      totalMinutes += duration;

      // Toutes les TimeEntry sont des entrées manuelles = Project Time
      projectTimeMinutes += duration;

      const current = projectMap.get(entry.projectName) || 0;
      projectMap.set(entry.projectName, current + duration);
    });

    // Calculer le Captured Time à partir des TimelineTask (activités Memtime)
    tasks.forEach(task => {
      // Duration est en secondes dans TimelineTask
      const duration = task.duration / 60; // Convertir en minutes
      capturedTimeMinutes += duration;
    });

    const totalHours = Math.floor(totalMinutes / 60);
    const remainingMinutes = Math.floor(totalMinutes % 60);
    const formattedTotal = totalHours > 0
      ? `${totalHours}h ${remainingMinutes}m`
      : `${remainingMinutes}m`;

    const projectTimeHours = Math.floor(projectTimeMinutes / 60);
    const projectTimeRemainingMinutes = Math.floor(projectTimeMinutes % 60);
    const formattedProjectTime = projectTimeHours > 0
      ? `${projectTimeHours}h ${projectTimeRemainingMinutes}m`
      : `${projectTimeRemainingMinutes}m`;

    const capturedTimeHours = Math.floor(capturedTimeMinutes / 60);
    const capturedTimeRemainingMinutes = Math.floor(capturedTimeMinutes % 60);
    const formattedCapturedTime = capturedTimeHours > 0
      ? `${capturedTimeHours}h ${capturedTimeRemainingMinutes}m`
      : `${capturedTimeRemainingMinutes}m`;

    const projectBreakdown = Array.from(projectMap.entries()).map(([projectName, minutes]) => {
      const hours = Math.floor(minutes / 60);
      const mins = Math.floor(minutes % 60);
      const percentage = totalMinutes > 0 ? (minutes / totalMinutes) * 100 : 0;
      return {
        projectName,
        hours,
        minutes: mins,
        formattedDuration: hours > 0 ? `${hours}h ${mins}m` : `${mins}m`,
        percentage
      };
    }).sort((a, b) => (b.hours * 60 + b.minutes) - (a.hours * 60 + a.minutes));

    return {
      date,
      dateString: this.getLocalDateString(date),
      totalHours,
      totalMinutes: remainingMinutes,
      formattedTotal,
      projectTimeHours,
      projectTimeMinutes: projectTimeRemainingMinutes,
      formattedProjectTime,
      capturedTimeHours,
      capturedTimeMinutes: capturedTimeRemainingMinutes,
      formattedCapturedTime,
      entries,
      projectBreakdown
    };
  }

  private getLocalDateString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getWeekTotal(): string {
    const total = this.dailySummaries.reduce((sum, day) => {
      return sum + (day.totalHours * 60 + day.totalMinutes);
    }, 0);
    const hours = Math.floor(total / 60);
    const minutes = Math.floor(total % 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  }

  getAveragePerDay(): string {
    const total = this.dailySummaries.reduce((sum, day) => {
      return sum + (day.totalHours * 60 + day.totalMinutes);
    }, 0);
    const avg = total / Math.max(this.dailySummaries.length, 1);
    const hours = Math.floor(avg / 60);
    const minutes = Math.floor(avg % 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  }

  getWeekRangeLabel(): string {
    if (this.dailySummaries.length === 0) return '';
    const firstDay = this.dailySummaries[0].date;
    const lastDay = this.dailySummaries[this.dailySummaries.length - 1].date;
    const firstMonth = firstDay.toLocaleDateString('en-US', { month: 'short' });
    const lastMonth = lastDay.toLocaleDateString('en-US', { month: 'short' });

    if (firstMonth === lastMonth) {
      return `${firstMonth} ${firstDay.getDate()}-${lastDay.getDate()}`;
    }
    return `${firstMonth} ${firstDay.getDate()} - ${lastMonth} ${lastDay.getDate()}`;
  }

  onDayClick(summary: DailySummary) {
    // Future: Navigate to that day's timeline or show details
    console.log('Day clicked:', summary.dateString);
  }

  onDateChange(newDate: Date) {
    this.selectedDate = newDate;
    this.calculateWeekStart();
    this.loadData();
  }

  navigateWeek(direction: number) {
    this.selectedWeekStart.setDate(this.selectedWeekStart.getDate() + (direction * 7));
    this.selectedDate = new Date(this.selectedWeekStart);
    this.loadData();
  }

  goToToday() {
    this.selectedDate = new Date();
    this.selectedDate.setHours(0, 0, 0, 0);
    this.calculateWeekStart();
    this.loadData();
  }

  getDayName(date: Date): string {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  }


  getProjectColor(index: number): string {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-orange-500',
      'bg-pink-500',
      'bg-teal-500',
      'bg-indigo-500',
      'bg-red-500'
    ];
    return colors[index % colors.length];
  }
}





